import {
  ObservaErrorBoundary,
  ObservaProvider,
  useObservaGlobalHandlers,
} from "@morganisbatman/observa-client-react";
import { useFonts } from "expo-font";
import { Stack } from "expo-router";
import { useEffect } from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";

import { AuthProvider, getCurrentUserId } from "../src/auth";
import { brandFontMap } from "../src/fonts";
import { useAppObserva } from "../src/observa/use-app-observa";
import { setObservaReporter } from "../src/observa/reporter";
import { PromocodeProvider } from "../src/promocode";
import { OBSERVA_ENDPOINT, OBSERVA_PUBLIC_KEY } from "../src/env";
import { colors, radius, space } from "../src/theme";

// observa captures the error + component stack before this renders; we just
// give the user something recoverable.
function renderErrorFallback(_err: Error, reset: () => void): JSX.Element {
  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.background,
        padding: space.lg,
        gap: space.md,
      }}
    >
      <Text style={{ fontSize: 18, fontWeight: "700", color: colors.textPrimary }}>
        Something went wrong.
      </Text>
      <Text
        style={{ fontSize: 14, color: colors.textMuted, textAlign: "center" }}
      >
        We&apos;ve logged the error. Try again, or reload the app.
      </Text>
      <Pressable
        onPress={reset}
        accessibilityRole="button"
        accessibilityLabel="Try again"
        style={{
          backgroundColor: colors.brand,
          paddingHorizontal: space.lg,
          paddingVertical: space.sm,
          borderRadius: radius.md,
        }}
      >
        <Text style={{ color: "#fff", fontWeight: "600" }}>Try again</Text>
      </Pressable>
    </View>
  );
}

// Global handlers (unhandled errors/rejections, session + page-view capture)
// need the client from the provider, so they live in a child component.
function ObservaWiring(): null {
  useObservaGlobalHandlers({ captureSessions: true, capturePageViews: true });
  // Expose the live client to module-level code (the tRPC client) so swallowed
  // network failures get reported instead of vanishing into a .catch.
  const observa = useAppObserva();
  useEffect(() => {
    setObservaReporter(observa);
    return () => setObservaReporter(null);
  }, [observa]);
  return null;
}

// Root layout. ObservaProvider wraps everything so screens can emit telemetry
// and the error boundary catches render crashes; AuthProvider exposes the
// session via useAuth(). Headers hidden; screens own their own chrome.
export default function RootLayout(): JSX.Element {
  // Brand fonts must be ready before the first paint so headings/figures don't
  // flash a system fallback. Hold a cream splash until they load.
  const [fontsLoaded] = useFonts(brandFontMap);

  if (!fontsLoaded) {
    return (
      <View
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.cream,
        }}
      >
        <ActivityIndicator color={colors.honey} />
      </View>
    );
  }

  const app = (
    <AuthProvider>
      <PromocodeProvider>
        <Stack screenOptions={{ headerShown: false }} />
      </PromocodeProvider>
    </AuthProvider>
  );

  // Only wire observa when a public ingest key is configured. Locally there's
  // no key (no apps/app/.env), so we skip it entirely — otherwise the SDK posts
  // to a non-existent endpoint, throws, and trips the dev error overlay.
  if (OBSERVA_PUBLIC_KEY === undefined || OBSERVA_PUBLIC_KEY === "") {
    return app;
  }

  return (
    <ObservaProvider
      config={{
        appId: "aucashapp",
        appVersion: process.env.EXPO_PUBLIC_APP_VERSION ?? "dev",
        environment: __DEV__ ? "development" : "production",
        endpoint: OBSERVA_ENDPOINT,
        key: OBSERVA_PUBLIC_KEY,
        // Tag every event/error with the signed-in user (or '0' when anon), so
        // user-scoped funnels and cohorts resolve instead of collapsing onto
        // the anonymous sentinel. Backed by a module store AuthProvider keeps
        // current (src/auth.tsx).
        getUserId: getCurrentUserId,
      }}
    >
      <ObservaWiring />
      <ObservaErrorBoundary fallback={renderErrorFallback}>{app}</ObservaErrorBoundary>
    </ObservaProvider>
  );
}
